import React from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
// Mock data for campaign conversion funnel
const conversionData = [{
  name: 'Views',
  value: 5200,
  color: '#6E9FD9'
}, {
  name: 'Clicks',
  value: 3800,
  color: '#D9984B'
}, {
  name: 'Applied',
  value: 2100,
  color: '#9C7CB6'
}, {
  name: 'Completed',
  value: 1450,
  color: '#7FC29B'
}];
// Mock data for campaign code usage over time (last 30 days)
const generateCodeUsageData = () => {
  return Array.from({
    length: 30
  }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (29 - i));
    // Create a pattern with some variability
    let usageCount = 30 + Math.floor(Math.random() * 40);
    // Add a peak in the middle
    if (i > 10 && i < 20) {
      usageCount += 20 + Math.floor(Math.random() * 30);
    }
    return {
      date: date.toISOString().split('T')[0],
      count: usageCount
    };
  });
};
// Mock data for payment volume over time (last 30 days)
const generatePaymentVolumeData = () => {
  return Array.from({
    length: 30
  }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (29 - i));
    // Create a pattern with some variability
    let amount = 1500 + Math.floor(Math.random() * 1000);
    // Add a peak in the middle
    if (i > 10 && i < 20) {
      amount += 1000 + Math.floor(Math.random() * 1500);
    }
    return {
      date: date.toISOString().split('T')[0],
      amount: amount
    };
  });
};
// Format date for display
const formatDate = dateStr => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};
// Conversion Funnel Chart
export const ConversionFunnelChart = () => {
  const funnelSummary = conversionData.map(d => `${d.name} ${d.value.toLocaleString()}`).join(', ');
  return <div className="h-80" role="img" aria-label={`Conversion funnel: ${funnelSummary}`} data-id="element-10897">
      <ResponsiveContainer width="100%" height="100%" data-id="element-10898">
        <BarChart data={conversionData} layout="vertical" margin={{
        top: 20,
        right: 30,
        left: 40,
        bottom: 5
      }} data-id="element-10899">
          <CartesianGrid strokeDasharray="3 3" data-id="element-10900" />
          <XAxis type="number" data-id="element-10901" />
          <YAxis dataKey="name" type="category" data-id="element-10902" />
          <Tooltip formatter={value => [`${value.toLocaleString()}`, 'Count']} data-id="element-10903" />

          <Legend data-id="element-10904" />
          <Bar dataKey="value" name="Users" data-id="element-10905">
            {conversionData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-10906" />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>;
};
// Conversion Rate Pie Chart
export const ConversionRatePieChart = () => {
  const {
    t
  } = useTranslation('shops');
  // Calculate conversion rates
  const viewToClick = (conversionData[1].value / conversionData[0].value * 100).toFixed(1);
  const clickToApply = (conversionData[2].value / conversionData[1].value * 100).toFixed(1);
  const applyToComplete = (conversionData[3].value / conversionData[2].value * 100).toFixed(1);
  const overallConversion = (conversionData[3].value / conversionData[0].value * 100).toFixed(1);
  const rateData = [{
    name: 'View → Click',
    value: parseFloat(viewToClick),
    color: '#6E9FD9'
  }, {
    name: 'Click → Apply',
    value: parseFloat(clickToApply),
    color: '#D9984B'
  }, {
    name: 'Apply → Complete',
    value: parseFloat(applyToComplete),
    color: '#9C7CB6'
  }];
  const rateSummary = rateData.map(d => `${d.name} ${d.value}%`).join(', ');
  return <div className="h-80" role="img" aria-label={`Conversion rates: ${rateSummary}. Overall conversion ${overallConversion}%.`} data-id="element-10907">
      <ResponsiveContainer width="100%" height="100%" data-id="element-10908">
        <PieChart data-id="element-10909">
          <Pie data={rateData} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value" label={({
          name,
          percent
        }) => `${name}: ${(percent * 100).toFixed(0)}%`} data-id="element-10910">
            {rateData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-10911" />)}
          </Pie>
          <Tooltip formatter={value => [`${value}%`, 'Conversion Rate']} data-id="element-10912" />
          <Legend data-id="element-10913" />
        </PieChart>
      </ResponsiveContainer>
      <div className="text-center mt-2 text-sm font-medium text-gray-700" data-id="element-10914">
        {t('components.ConversionRatePieChart.overall_conversion')}{' '}
        {overallConversion}%
      </div>
    </div>;
};
// Code Usage Time Series Chart
export const CodeUsageTimeSeriesChart = () => {
  const codeUsageData = generateCodeUsageData();
  const total = codeUsageData.reduce((s, d) => s + d.count, 0);
  return <div className="h-80" role="img" aria-label={`Promo code usage over the last 30 days. Total uses: ${total.toLocaleString()}.`} data-id="element-10915">
      <ResponsiveContainer width="100%" height="100%" data-id="element-10916">
        <AreaChart data={codeUsageData} margin={{
        top: 10,
        right: 30,
        left: 0,
        bottom: 0
      }} data-id="element-10917">
          <CartesianGrid strokeDasharray="0" vertical={false} data-id="element-10918" />
          <XAxis dataKey="date" tickFormatter={formatDate} interval={4} data-id="element-10919" />
          <YAxis axisLine={false} data-id="element-10920" />
          <Tooltip formatter={value => [value.toLocaleString(), 'Usage Count']} labelFormatter={label => formatDate(label)} data-id="element-10921" />

          <Legend data-id="element-10922" />
          <Area type="monotone" dataKey="count" stroke="#5DAFBA" fill="#5DAFBA" fillOpacity={0.3} name="Code Usage" data-id="element-10923" />
        </AreaChart>
      </ResponsiveContainer>
    </div>;
};
// Payment Volume Time Series Chart
export const PaymentVolumeTimeSeriesChart = ({
  currency = 'MYR'
}) => {
  const paymentVolumeData = generatePaymentVolumeData();
  const total = paymentVolumeData.reduce((s, d) => s + d.amount, 0);
  return <div className="h-80" role="img" aria-label={`Payment volume over the last 30 days. Total: ${currency} ${total.toLocaleString()}.`} data-id="element-10924">
      <ResponsiveContainer width="100%" height="100%" data-id="element-10925">
        <AreaChart data={paymentVolumeData} margin={{
        top: 10,
        right: 30,
        left: 0,
        bottom: 0
      }} data-id="element-10926">
          <CartesianGrid strokeDasharray="0" vertical={false} data-id="element-10927" />
          <XAxis dataKey="date" tickFormatter={formatDate} interval={4} data-id="element-10928" />
          <YAxis axisLine={false} data-id="element-10929" />
          <Tooltip formatter={value => [`${currency} ${value.toLocaleString('en-US')}`, 'Volume']} labelFormatter={label => formatDate(label)} data-id="element-10930" />

          <Legend data-id="element-10931" />
          <Area type="monotone" dataKey="amount" stroke="#7FC29B" fill="#7FC29B" fillOpacity={0.3} name="Payment Volume" data-id="element-10932" />
        </AreaChart>
      </ResponsiveContainer>
    </div>;
};